"use client";

import type { ReactNode } from "react";

import Link from "next/link";

import { MoreHorizontal, Settings, type LucideIcon } from "lucide-react";

import { usePathname } from "next/navigation";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

export type QuickViewOption = {
  key: string;
  label: string;
  icon: LucideIcon;
};

export function NavProjects({
  projects,
}: {
  projects: {
    key: string;
    name: string;
    url: string;
    icon: LucideIcon;
    indicator?: ReactNode;
    quickViews: QuickViewOption[];
  }[];
}) {
  const { isMobile } = useSidebar();
  const pathname = usePathname();

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Dashboards</SidebarGroupLabel>
      <SidebarMenu>
        {projects.map((item) => (
          <SidebarMenuItem key={item.key}>
            <SidebarMenuButton asChild tooltip={item.name}>
              <Link
                href={item.url}
                data-active={pathname === item.url}
                className="flex items-center gap-2 data-[active=true]:bg-sidebar-accent/40 data-[active=true]:text-sidebar-foreground"
              >
                <item.icon />
                <span className="flex-1 truncate">{item.name}</span>
                {item.indicator ? <span className="ml-1 flex items-center">{item.indicator}</span> : null}
              </Link>
            </SidebarMenuButton>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuAction showOnHover>
                  <MoreHorizontal />
                  <span className="sr-only">More</span>
                </SidebarMenuAction>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-48 rounded-lg"
                side={isMobile ? "bottom" : "right"}
                align={isMobile ? "end" : "start"}
              >
                {item.quickViews.map((view) => (
                  <DropdownMenuItem key={view.key}>
                    <view.icon className="text-muted-foreground" />
                    <span>{view.label}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Settings className="text-muted-foreground" />
                  <span>Preferences</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
